/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import frLocale from 'date-fns/locale/fr';
import esLocale from 'date-fns/locale/es';
import enLocale from 'date-fns/locale/en-US';
import AdapterDateFns from '@mui/lab/AdapterLuxon';
import makeStyles from '@mui/styles/makeStyles';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import StaticDatePicker from '@mui/lab/StaticDatePicker';
import { DateTime } from 'luxon';
import { styled } from '@mui/material/styles';
import PickersDay from '@mui/lab/PickersDay';
import { FormHelperText, Box } from '@mui/material';
import { TextField } from 'components/elements';
import Styles from './styles';

const useStyles = makeStyles(Styles);

const CustomPickersDay = styled(PickersDay)(({ theme }) => ({
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: '50%',
  color: theme.palette.primary.main,
  margin: '2px 0',
  '&:hover, &:focus': {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.common.white,
  },
}));

const configs = {
  en: { localeMap: enLocale },
  fr: { localeMap: frLocale },
  es: { localeMap: esLocale },
};

function CustomStaticDatePicker({ helperText, value, onChange, onMonthChange, availableDays, onViewChange }) {
  const classes = useStyles();
  const { i18n } = useTranslation();
  const todayDate = DateTime.now();
  const config = useMemo(() => ({ locale: i18n.language, ...(configs[i18n.language] || configs.en) }), [i18n.language]);
  const renderWeekPickerDay = (date, selectedDates, pickersDayProps) => {
    if (availableDays.some((x) => x.ordinal === date.ordinal)) {
      return <CustomPickersDay {...pickersDayProps} />;
    }
    return <PickersDay {...pickersDayProps} />;
  };

  const val = useMemo(() => {
    if (!value) {
      return value;
    }
    if (value instanceof Date) {
      return DateTime.fromJSDate(value);
    }
    return value;
  }, [value]);

  return (
    <Box data-testid="staticDatePicker" className={classes.staticPickerWrapper}>
      <LocalizationProvider dateAdapter={AdapterDateFns} locale={config.localeMap.code}>
        <StaticDatePicker
          renderDay={renderWeekPickerDay}
          reduceAnimations
          displayStaticWrapperAs="desktop"
          openTo="day"
          views={['year', 'month', 'day']}
          value={val}
          allowSameDateSelection
          disableHighlightToday
          onChange={onChange}
          onMonthChange={onMonthChange}
          onViewChange={onViewChange}
          minDate={todayDate}
          renderInput={(params) => <TextField {...params} />}
        />
        <FormHelperText sx={{ color: 'red' }}>{helperText}</FormHelperText>
      </LocalizationProvider>
    </Box>
  );
}

CustomStaticDatePicker.propTypes = {
  label: PropTypes.element.isRequired,
  error: PropTypes.bool.isRequired,
  helperText: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.instanceOf(DateTime), PropTypes.instanceOf(Date)]),
  onChange: PropTypes.func.isRequired,
  onMonthChange: PropTypes.func.isRequired,
  availableDays: PropTypes.arrayOf(PropTypes.any),
  onViewChange: PropTypes.func,
};

CustomStaticDatePicker.defaultProps = {
  value: null,
  helperText: null,
  availableDays: [],
  onViewChange: () => {},
};

export default CustomStaticDatePicker;
